import React from 'react';
import AppHeader from '../components/AppHeader';
import StorePicker from '../components/StorePicker';
import PickupSection from '../components/PickupSection';
import CopyListButton from '../components/CopyListButton';
import { useOrder } from '../state/OrderContext';
import { useRecipeLibrary } from '../state/RecipeLibraryContext';
import { describeMatch } from '../lib/matchRecipes';
import { formatShoppingList } from '../lib/shoppingList';
import { countFlags, describeFlags } from '../lib/recipeFlags';
import {
  BasketIcon,
  BookmarkIcon,
  ChevronRightIcon,
  ClockIcon,
  MinusIcon,
  PlayIcon,
  PlusIcon,
  PotIcon,
  QueryIcon,
  TrashIcon,
  UndoIcon,
} from '../components/icons';
import './Recipe.css';

// Recipe — everything about the meal in one place: the recipe itself, the
// shopping list derived from it, and nearby shops to buy it from. Finding
// those shops needs a location and a network call, so it lives in its own
// component; this screen just gives it a place to sit.
//
// The ingredient list and the shopping list are deliberately not the same
// thing. Taking an ingredient off the list changes what the user buys; the
// recipe still calls for it, and says so.
//
// Where a recipe came from a paste or a link, some of it may be uncertain.
// This screen shows exactly which parts, and lets the user answer.

function sourceLine(recipe, match) {
  const matched = describeMatch(match);
  if (matched) return matched;
  const { creator, siteName, label } = recipe.origin ?? {};
  if (creator && siteName && creator !== siteName) return `${creator} · ${siteName}`;
  return `From ${creator ?? siteName ?? label ?? 'your description'}`;
}

export default function Recipe({ onBack, onDone }) {
  const {
    recipe,
    match,
    servings,
    ingredients,
    shoppingList,
    steps,
    playback,
    setServings,
    addIngredient,
    editIngredient,
    editStep,
    removeIngredient,
    restoreIngredient,
  } = useOrder();
  const { getRecipe, setSaved } = useRecipeLibrary();

  // A player renders only when there is video to play. Origin does not decide
  // this — a recipe found on the web can carry video, and a video link can
  // resolve to a recipe with no usable media.
  //
  // The two sources are kept apart on purpose. `media.videoUrl` is a durable
  // address that will still work tomorrow; `playback` is a file the user
  // picked this session, which we can show but did not read. Only the first
  // belongs to the recipe.
  const sourceVideo = recipe.media?.videoUrl ?? null;
  const attachedVideo = sourceVideo ? null : playback;

  const isSaved = Boolean(getRecipe(recipe.id)?.savedAt);
  const excludedCount = ingredients.length - shoppingList.length;
  const flags = countFlags({ ingredients, steps });
  const flagSummary = describeFlags(flags);
  const originUrl = recipe.origin?.url ?? null;

  // Built from the shopping list, not the ingredient list — copying is a
  // purchasing action, so an excluded row is not in it. Recomputed on every
  // render so an amount typed a second ago is in the text that gets copied.
  const listText = formatShoppingList(shoppingList, { dishName: recipe.dishName, servings });

  return (
    <div className="screen">
      <div className="screen__inner">
        <AppHeader onBack={onBack} />

        <div className="recipe-title-row">
          <h1 className="screen__title recipe-title">{recipe.dishName}</h1>
          <button
            type="button"
            className={`recipe-save${isSaved ? ' recipe-save--on' : ''}`}
            aria-pressed={isSaved}
            aria-label={isSaved ? 'Remove from saved recipes' : 'Save this recipe'}
            title={isSaved ? 'Saved' : 'Save this recipe'}
            onClick={() => setSaved(recipe.id, !isSaved)}
          >
            <BookmarkIcon size={18} filled={isSaved} />
          </button>
        </div>

        {/* Attribution lives on `origin` and nowhere else, so there is only one
            statement of where this came from and only one link to follow. */}
        {originUrl ? (
          <a className="source-badge source-badge--link" href={originUrl} target="_blank" rel="noreferrer">
            {sourceLine(recipe, match)} <ChevronRightIcon size={14} />
          </a>
        ) : (
          <span className="source-badge">{sourceLine(recipe, match)}</span>
        )}

        {sourceVideo ? (
          <button
            type="button"
            className="recipe-video photo-placeholder"
            aria-label={`Play ${recipe.dishName} video`}
          >
            <span className="recipe-video__play">
              <PlayIcon size={22} />
            </span>
          </button>
        ) : attachedVideo ? (
          <figure className="recipe-attached">
            <video className="recipe-attached__player" src={attachedVideo.url} controls playsInline />
            {/* Said plainly, because the alternative is letting a playing
                video imply we understood it. We didn't. */}
            <figcaption className="recipe-attached__note">
              Your video, for reference. We couldn&rsquo;t read a recipe out of it — what&rsquo;s below is
              what you wrote.
            </figcaption>
          </figure>
        ) : (
          <div className="recipe-photo photo-placeholder" aria-hidden="true">
            <PotIcon size={30} />
          </div>
        )}

        <div className="recipe-facts">
          <span className="meta-row">
            <ClockIcon size={15} />{' '}
            {recipe.totalMinutes ? `${recipe.totalMinutes} min` : <em className="unknown">time not given</em>}
          </span>
          <span className="servings">
            <button
              type="button"
              className="servings__step"
              aria-label="Fewer servings"
              disabled={servings <= 1}
              onClick={() => setServings(servings - 1)}
            >
              <MinusIcon size={14} />
            </button>
            <span className="servings__value">
              {servings} <span className="servings__unit">servings</span>
            </span>
            <button
              type="button"
              className="servings__step"
              aria-label="More servings"
              disabled={servings >= 12}
              onClick={() => setServings(servings + 1)}
            >
              <PlusIcon size={14} />
            </button>
          </span>
        </div>

        {recipe.servings == null && (
          <p className="recipe-breakdown">
            The source didn&rsquo;t say how many this serves — set it above.
          </p>
        )}

        {(recipe.prepMinutes || recipe.cookMinutes) && (
          <p className="recipe-breakdown">
            {recipe.prepMinutes ? `${recipe.prepMinutes} min prep` : null}
            {recipe.prepMinutes && recipe.cookMinutes ? ' · ' : null}
            {recipe.cookMinutes ? `${recipe.cookMinutes} min cook` : null}
          </p>
        )}

        {/* What we could not read, counted. Nothing here was guessed at; every
            flagged row below is waiting on the user, not on us. */}
        {flagSummary && (
          <p className="flag-summary">
            <QueryIcon size={15} /> {flags.total} to check: {flagSummary}.
          </p>
        )}

        <section className="card">
          <div className="shopping-head">
            <p className="card__label shopping-head__label">
              <BasketIcon size={16} /> Shopping list
            </p>
            <span className="shopping-head__count">
              {shoppingList.length} of {ingredients.length}
            </span>
          </div>

          <ul className="ingredient-list">
            {ingredients.map((item) => (
              <li
                key={item.id}
                className={`ingredient${item.included ? '' : ' ingredient--excluded'}${
                  item.quantityMissing ? ' ingredient--flagged' : ''
                }`}
              >
                {item.included ? (
                  <>
                    <input
                      className="ingredient__name"
                      value={item.name}
                      placeholder="Ingredient"
                      aria-label="Ingredient name"
                      onChange={(event) => editIngredient(item.id, { name: event.target.value })}
                    />
                    <input
                      className={`ingredient__qty${item.quantityMissing ? ' ingredient__qty--flagged' : ''}`}
                      value={item.quantity}
                      placeholder={item.quantityMissing ? 'how much?' : 'Qty'}
                      aria-label={
                        item.quantityMissing
                          ? `Amount for ${item.name || 'ingredient'} — not given in the source`
                          : 'Quantity'
                      }
                      title={item.quantityMissing ? "The source didn't give an amount" : undefined}
                      onChange={(event) => editIngredient(item.id, { quantity: event.target.value })}
                    />
                    <button
                      type="button"
                      className="ingredient__remove"
                      aria-label={`Take ${item.name || 'ingredient'} off the shopping list`}
                      onClick={() => removeIngredient(item.id)}
                    >
                      <TrashIcon size={15} />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="ingredient__name ingredient__name--static">{item.name}</span>
                    <span className="ingredient__qty ingredient__qty--static">{item.quantity}</span>
                    <button
                      type="button"
                      className="ingredient__restore"
                      aria-label={`Put ${item.name || 'ingredient'} back on the shopping list`}
                      onClick={() => restoreIngredient(item.id)}
                    >
                      <UndoIcon size={15} />
                    </button>
                  </>
                )}
              </li>
            ))}
          </ul>

          {ingredients.length === 0 && (
            <p className="ingredient-empty">No ingredients yet — add what you need.</p>
          )}

          {excludedCount > 0 && (
            <p className="shopping-note">
              {excludedCount} not on your list. The recipe still calls for {excludedCount === 1 ? 'it' : 'them'}.
            </p>
          )}

          <div className="shopping-foot">
            <button type="button" className="ingredient-add" onClick={addIngredient}>
              <PlusIcon size={15} /> Add ingredient
            </button>
            {/* The list has to be able to leave the app — nothing here sends
                it to a shop. Disabled rather than hidden when there is nothing
                on it, so the button doesn't appear and disappear. */}
            <CopyListButton text={listText} disabled={listText === ''} />
          </div>
        </section>

        <StorePicker />
        <PickupSection />

        <section className="card">
          <p className="card__label">{steps.length} steps</p>
          {/* Only steps we could not read cleanly are editable. The rest stay
              as text so the recipe still reads like a recipe rather than a
              form — and so an edit box means something when you see one. */}
          <ol className="recipe-steps">
            {steps.map((step) => (
              <li key={step.id} className={step.unclear ? 'recipe-step--flagged' : undefined}>
                {step.unclear ? (
                  <>
                    <span className="recipe-step__flag">
                      <QueryIcon size={13} /> unclear in the source — fix it here
                    </span>
                    <textarea
                      className="recipe-step__edit"
                      value={step.text}
                      rows={2}
                      aria-label="Unclear step — edit it"
                      onChange={(event) => editStep(step.id, event.target.value)}
                    />
                  </>
                ) : (
                  step.text
                )}
              </li>
            ))}
          </ol>
          {steps.length === 0 && <p className="ingredient-empty">No steps came through with this one.</p>}
        </section>

        <button type="button" className="button button--primary" onClick={onDone}>
          Mark as made
        </button>
      </div>
    </div>
  );
}
